import React from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { Minus, Plus, X } from 'lucide-react';

export default function Cart() {
  const { t, cart, updateQty, removeFromCart } = useApp();
  const subtotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);

  if (cart.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-32 text-center" data-testid="cart-empty">
        <h1 className="font-display text-5xl">{t('cart.empty')}</h1>
        <Link to="/shop" className="mt-10 inline-block bg-foreground text-background px-8 py-4 overline" data-testid="cart-shop-link">{t('cta.shop')}</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16" data-testid="cart-page">
      <h1 className="font-display text-4xl sm:text-5xl mb-12">{t('cart.title')}</h1>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 space-y-8">
          {cart.map((i) => (
            <div key={i.id} className="grid grid-cols-12 gap-4 border-b border-border pb-8" data-testid={`cart-item-${i.id}`}>
              <Link to={`/product/${i.id}`} className="col-span-3">
                <div className="aspect-[4/5] bg-muted overflow-hidden">
                  {i.image && <img src={i.image} alt={i.title} className="w-full h-full object-cover" />}
                </div>
              </Link>
              <div className="col-span-9 flex flex-col">
                <div className="flex justify-between items-start gap-4">
                  <div>
                    <div className="overline text-muted-foreground">{i.designer}</div>
                    <Link to={`/product/${i.id}`} className="font-display text-xl mt-1 link-underline block">{i.title}</Link>
                  </div>
                  <button onClick={() => removeFromCart(i.id)} aria-label="remove" data-testid={`cart-remove-${i.id}`}><X size={16} strokeWidth={1.3} /></button>
                </div>
                <div className="mt-auto flex justify-between items-end pt-6">
                  <div className="inline-flex items-center border border-border">
                    <button onClick={() => updateQty(i.id, i.quantity - 1)} className="p-2" data-testid={`qty-minus-${i.id}`}><Minus size={12} /></button>
                    <span className="px-4 text-sm" data-testid={`qty-${i.id}`}>{i.quantity}</span>
                    <button onClick={() => updateQty(i.id, i.quantity + 1)} className="p-2" data-testid={`qty-plus-${i.id}`}><Plus size={12} /></button>
                  </div>
                  <div className="font-display text-xl">${(i.price * i.quantity).toLocaleString()}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="lg:col-span-4">
          <div className="border border-border p-8 sticky top-32" data-testid="cart-summary">
            <div className="overline mb-6">Order Summary</div>
            <div className="flex justify-between font-light"><span>{t('cart.subtotal')}</span><span data-testid="cart-subtotal">${subtotal.toLocaleString()}</span></div>
            <div className="text-xs text-muted-foreground font-light mt-2">{t('cart.shipping')}</div>
            <div className="border-t border-border my-6"></div>
            <div className="flex justify-between font-display text-xl"><span>{t('cart.total')}</span><span data-testid="cart-total">${subtotal.toLocaleString()}</span></div>
            <Link to="/checkout" className="mt-8 block text-center bg-foreground text-background px-8 py-4 overline hover:opacity-80 transition" data-testid="checkout-btn">{t('cta.checkout')} →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
